/**
 * React Query hook for organization users
 *
 * Provides data fetching for users within an organization with pagination support.
 * Uses React Query for caching to enable instant tab switches.
 */

import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { UserApiService } from '../services/userApi';
import type { TenantUser } from '../types/user';

/**
 * Query key factory for organization users
 */
export const organizationUsersKeys = {
  all: ['organizationUsers'] as const,
  list: (params: { organizationId?: number; tenantId?: string; page: number; pageSize: number }) =>
    ['organizationUsers', params.organizationId ?? null, params.tenantId ?? null, { page: params.page, pageSize: params.pageSize }] as const,
};

/**
 * Parameters for useOrganizationUsers hook
 */
export interface UseOrganizationUsersParams {
  /** Organization ID to fetch users for */
  organizationId: number | undefined;
  /** Current page number (1-based) */
  page?: number;
  /** Number of items per page */
  pageSize?: number;
  /** Optional tenant ID override (for direct tenant access) */
  tenantId?: string;
}

/**
 * Hook return type for useOrganizationUsers
 */
export interface UseOrganizationUsersResult {
  /** User list for the current page */
  users: TenantUser[] | undefined;
  /** Total number of users across all pages */
  total: number;
  /** Loading state for initial fetch */
  isLoading: boolean;
  /** Fetching state (true during refetch) */
  isFetching: boolean;
  /** Syncing state (true during syncUsers call) */
  isSyncing: boolean;
  /** Error from fetching users */
  error: Error | null;
  /** Refetch users (uses cache, sync=false) */
  refetch: () => Promise<void>;
  /** Sync users with Azure AD (sync=true, bypasses cache) */
  syncUsers: () => Promise<void>;
}

/**
 * React Query hook for fetching organization users with pagination
 *
 * @param params - Hook parameters including organizationId and pagination
 * @returns Users data, loading states, and refetch/sync functions
 *
 * @example
 * ```tsx
 * const { users, isLoading, total, syncUsers } = useOrganizationUsers({
 *   organizationId: 1,
 *   page: 1,
 *   pageSize: 20,
 * });
 *
 * // Refresh from Azure AD
 * await syncUsers();
 * ```
 */
export function useOrganizationUsers({
  organizationId,
  page = 1,
  pageSize = 20,
  tenantId,
}: UseOrganizationUsersParams): UseOrganizationUsersResult {
  const queryClient = useQueryClient();
  const [isSyncing, setIsSyncing] = useState(false);

  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch: queryRefetch,
  } = useQuery({
    queryKey: organizationUsersKeys.list({
      organizationId,
      tenantId,
      page,
      pageSize,
    }),
    queryFn: async () => {
      // sync=false for normal fetches (use backend cache)
      const response = await UserApiService.listUsers(
        tenantId,
        page,
        pageSize,
        '', // searchQuery - handled client-side
        false, // sync=false for cached fetch
        organizationId
      );
      return response;
    },
    // Enable when organizationId is provided OR tenantId is provided
    enabled: organizationId !== undefined || tenantId !== undefined,
  });

  /**
   * Refetch users from backend
   */
  const refetch = async () => {
    await queryRefetch();
  };

  /**
   * Force sync with Azure AD (bypasses cache)
   */
  const syncUsers = async () => {
    if (organizationId === undefined && tenantId === undefined) return;

    setIsSyncing(true);
    try {
      // Fetch with sync=true to force Azure AD refresh
      const response = await UserApiService.listUsers(
        tenantId,
        page,
        pageSize,
        '',
        true, // sync=true for Azure AD refresh
        organizationId
      );

      // Update the cache with the fresh data
      queryClient.setQueryData(
        organizationUsersKeys.list({ organizationId, tenantId, page, pageSize }),
        response
      );
    } finally {
      setIsSyncing(false);
    }
  };

  return {
    users: data?.items,
    total: data?.total ?? 0,
    isLoading,
    isFetching,
    isSyncing,
    error: error as Error | null,
    refetch,
    syncUsers,
  };
}
